import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import QRCodeGenerator from '../../components/session/QRCodeGenerator';
import ActivityResultsViewer from '../../components/results/ActivityResultsViewer';
import ParticipantListPanel from '../../components/trainer/ParticipantListPanel';
import ActivitySettingsPanel from '../../components/trainer/ActivitySettingsPanel';
import SessionControls from '../../components/trainer/SessionControls';
import LaunchSimulationPanel from '../../components/trainer/LaunchSimulationPanel';
import LaunchMicroSimPanel from '../../components/trainer/LaunchMicroSimPanel';
import { useSession } from '../../contexts/SessionContext';
import useWebSocket from '../../hooks/useWebSocket';
import { trainerService } from '../../services';
import exportService from '../../services/exportService';
import { exportSessionData, exportParticipantData } from '../../utils/exportSessionData';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Session Control Page - Trainer Live Control
 * Control live session, launch activities, monitor participants
 */

// Resolve activity timeLimit (seconds) from its various legacy fields.
// Require explicit `timerEnabled === true`. Timer is disabled by default; any
// activity that doesn't opt in is untimed, regardless of legacy duration data.
const resolveTimeLimitSec = (a) => {
  if (!a) return 0;
  if (a.timerEnabled !== true) return 0;
  if (typeof a.timeLimit === 'number' && a.timeLimit > 0) return a.timeLimit;
  if (a.config?.timeLimit > 0) return a.config.timeLimit;
  if (a.duration > 0) return a.duration * 60;
  return 0;
};

// Live MM:SS countdown chip for the trainer's currently active activity.
// Counts down from launchedAt + timeLimit. When it hits zero the server
// auto-closes and emits activity:closed; this UI flips via the existing
// socket handler that clears currentActivity.
// When `timerPausedAt` is set, the chip freezes at the paused remaining seconds
// and shows a "Paused" label.
const ActivityCountdown = ({ activity }) => {
  const seconds = resolveTimeLimitSec(activity);
  const launchedAt = activity?.launchedAt ? new Date(activity.launchedAt).getTime() : null;
  const pausedAt = activity?.timerPausedAt ? new Date(activity.timerPausedAt).getTime() : null;
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!seconds || !launchedAt || pausedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [seconds, launchedAt, pausedAt]);

  if (!seconds || !launchedAt) return null;
  // When paused, freeze the countdown at the time the pause began.
  const evalTs = pausedAt || now;
  const remaining = Math.max(0, Math.round((launchedAt + seconds * 1000 - evalTs) / 1000));
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const danger = !pausedAt && remaining <= 10;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold tabular-nums ${
        pausedAt
          ? 'bg-neutral-100 text-neutral-600'
          : remaining === 0
            ? 'bg-neutral-200 text-neutral-700'
            : danger
              ? 'bg-red-100 text-red-700 animate-pulse'
              : 'bg-amber-100 text-amber-800'
      }`}
      title={pausedAt ? 'Timer paused' : 'Time remaining (server-controlled)'}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {pausedAt
        ? `Paused ${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
        : remaining === 0
          ? 'Time up'
          : `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`}
    </span>
  );
};

// Trainer controls for the running activity's timer: pause/resume, +1 min, stop timer.
// Emits `activity:timer-control` over the session socket; the server updates the
// activity doc and broadcasts `activity:timer-updated` back to everyone.
const TimerControls = ({ activity, socket }) => {
  const seconds = resolveTimeLimitSec(activity);
  if (!seconds) return null; // no timer → nothing to control

  const isPaused = !!activity?.timerPausedAt;
  const activityId = activity?._id || activity?.id;
  const emit = (action, extra = {}) => {
    if (!socket || !activityId) return;
    socket.emit('activity:timer-control', { activityId, action, ...extra });
  };

  const btn = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors';
  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => emit(isPaused ? 'resume' : 'pause')}
        className={`${btn} bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200`}
        title={isPaused ? 'Resume timer' : 'Pause timer'}
      >
        {isPaused ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
        )}
        {isPaused ? 'Resume' : 'Pause'}
      </button>
      <button
        onClick={() => emit('extend', { seconds: 60 })}
        className={`${btn} bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200`}
        title="Add 1 minute"
      >
        +1 min
      </button>
      <button
        onClick={() => emit('stop')}
        className={`${btn} bg-neutral-50 hover:bg-neutral-100 text-neutral-700 border border-neutral-200`}
        title="Remove the timer (activity keeps running)"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Stop Timer
      </button>
    </div>
  );
};

const SessionControlPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { getSession, launchActivity: launchActivityInContext, closeActivity: closeActivityInContext } = useSession();
  const socket = useWebSocket(sessionId);

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [currentSubSession, setCurrentSubSession] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [participants, setParticipants] = useState(0);
  const [participantList, setParticipantList] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('0m');
  const [engagementScore, setEngagementScore] = useState(0);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showProjection, setShowProjection] = useState(false);
  const [showParticipantNames, setShowParticipantNames] = useState(false);
  const [activityResponses, setActivityResponses] = useState([]);
  const [showParticipantPanel, setShowParticipantPanel] = useState(false);
  const [showActivitySettings, setShowActivitySettings] = useState(false);
  const [selectedActivityForSettings, setSelectedActivityForSettings] = useState(null);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [endSessionMessage, setEndSessionMessage] = useState('');
  const [showEndSubsessionModal, setShowEndSubsessionModal] = useState(false);
  // Alert popup shown when trainer attempts to launch a second activity or
  // move to the next subsession while one is still active. `{ title, message }`.
  const [activityActiveAlert, setActivityActiveAlert] = useState(null);
  const [showWorkbookModal, setShowWorkbookModal] = useState(false);
  const [launchedWorkbook, setLaunchedWorkbook] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showEarlyStartWarning, setShowEarlyStartWarning] = useState(false);
  const [earlyStartMinutes, setEarlyStartMinutes] = useState(0);
  const [earlyStartSeconds, setEarlyStartSeconds] = useState(0);
  const [scheduledStartTime, setScheduledStartTime] = useState(null);
  const [sessionTimezone, setSessionTimezone] = useState('UTC');
  const [sessionEnded, setSessionEnded] = useState(false);

  // Break time tracking
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);
  const [currentBreakInfo, setCurrentBreakInfo] = useState({ name: '', breakTime: 0 });

  // Overall session feedback tracking
  const [overallFeedbackLaunched, setOverallFeedbackLaunched] = useState(false);
  const [overallFeedbackEnabled, setOverallFeedbackEnabled] = useState(false);
  const [showOverallFeedbackModal, setShowOverallFeedbackModal] = useState(false);
  const [overallFeedbackTrigger, setOverallFeedbackTrigger] = useState('manual'); // 'manual' or 'end'
  const [showEndSessionWarning, setShowEndSessionWarning] = useState(false);
  const [pendingEndSessionOptions, setPendingEndSessionOptions] = useState(null);
  const [overallFeedbackResponses, setOverallFeedbackResponses] = useState([]);

  // Re-launch confirmation dialog
  const [showRelaunchConfirm, setShowRelaunchConfirm] = useState(false);
  const [pendingRelaunchActivity, setPendingRelaunchActivity] = useState(null);

  // Delete activity confirmation dialog
  const [showDeleteActivityConfirm, setShowDeleteActivityConfirm] = useState(false);
  const [pendingDeleteActivity, setPendingDeleteActivity] = useState(null);
  const [deletingActivity, setDeletingActivity] = useState(false);

  // Activity creation form state
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [activityFormData, setActivityFormData] = useState({
    question: '',
    options: ['', ''],
    correctAnswer: 0,
    timeLimit: 300, // 5 minutes default in seconds
    allowMultipleResponses: false, // For word cloud - allow participants to submit multiple times
  });

  // Load session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true);
        const response = await trainerService.getSession(sessionId);
        if (response.success) {
          const foundSession = response.data;

          // Check if session has ended - prevent control
          if (foundSession.status === 'completed' || foundSession.status === 'cancelled') {
            setSessionEnded(true);
            setSession(foundSession);
            setLoading(false);
            return;
          }

          // Check if starting early (before scheduled start time)
          // Use startTime (full datetime) instead of scheduledDate (date only, midnight UTC)
          // Times are stored as pseudo-UTC (local time with Z suffix), so we must
          // convert current time to the session's timezone for accurate comparison
          const sessionStartTime = foundSession.startTime || foundSession.scheduledDate;
          if (sessionStartTime && foundSession.status !== 'live') {
            const scheduledTime = new Date(sessionStartTime);
            const sessionTz = foundSession.timezone || 'UTC';

            // Convert current browser time to session's timezone as pseudo-UTC
            const now = new Date();
            let pseudoUtcNow;
            try {
              const localTimeStr = now.toLocaleString('sv-SE', { timeZone: sessionTz });
              pseudoUtcNow = new Date(localTimeStr.replace(' ', 'T') + '.000Z');
            } catch {
              // Fallback if timezone is invalid - use raw UTC
              pseudoUtcNow = now;
            }

            const diffMs = scheduledTime - pseudoUtcNow;
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

            // If before scheduled time, show early start warning
            if (diffMs > 0) {
              setScheduledStartTime(scheduledTime);
              setSessionTimezone(sessionTz);
              setEarlyStartMinutes(diffMinutes);
              setEarlyStartSeconds(diffSeconds);
              setShowEarlyStartWarning(true);
            }
          }

          // Debug: Log workbooks
          console.log('[SessionControl] Session workbooks:', foundSession.workbooks);
          console.log('[SessionControl] Workbooks count:', foundSession.workbooks?.length || 0);

          // Restore launched workbook from session data (persisted by backend)
          if (foundSession.launchedWorkbook && foundSession.launchedWorkbook.url) {
            console.log('[SessionControl] Restoring launched workbook:', foundSession.launchedWorkbook);
            setLaunchedWorkbook(foundSession.launchedWorkbook);
          }

          setSession(foundSession);
          // Only set initial subsession if none is currently selected
          // This prevents resetting to first subsession when page refreshes or data updates
          if (foundSession.subSessions && foundSession.subSessions.length > 0) {
            setCurrentSubSession(prev => {
              // If we already have a current subsession, try to find and preserve it
              if (prev && (prev._id || prev.id)) {
                const prevId = prev._id || prev.id;
                const matchingSubSession = foundSession.subSessions.find(ss =>
                  (ss._id === prevId || ss.id === prevId)
                );
                // Return the updated version of the current subsession if found
                return matchingSubSession || foundSession.subSessions[0];
              }
              // Fresh page load (refresh, first mount): pick from backend status
              // so a refresh on subsession 2 doesn't snap back to subsession 1.
              // Priority: 'active' > first non-'completed' > first overall.
              const activeSub = foundSession.subSessions.find(ss => ss.status === 'active');
              if (activeSub) return activeSub;
              const firstNonCompleted = foundSession.subSessions.find(ss => ss.status !== 'completed');
              return firstNonCompleted || foundSession.subSessions[0];
            });
          }
          // Set participant count from session data - only count joined participants
          const joinedParticipants = foundSession.participants?.filter(p => p.status === 'joined') || [];
          setParticipants(foundSession.participantCount || joinedParticipants.length || 0);
          setParticipantList(joinedParticipants);
          // Set session start time for duration calculation
          // Use actualStartTime (when session went live) over scheduled startTime
          if (foundSession.actualStartTime) {
            setSessionStartTime(new Date(foundSession.actualStartTime));
          } else if (foundSession.status === 'live') {
            // If session is live but no actualStartTime, use current time
            setSessionStartTime(new Date());
          } else if (foundSession.startTime) {
            // For scheduled sessions, use scheduled start time
            setSessionStartTime(new Date(foundSession.startTime));
          }
          // Calculate engagement score from actual responses
          // Count only launched activities (not total activities)
          let launchedActivities = 0;
          let totalResponses = 0;
          foundSession.subSessions?.forEach(ss => {
            ss.activities?.forEach(act => {
              // Check if activity was launched
              const isLaunched = act.status === 'active' || act.status === 'completed' || act.status === 'closed' ||
                act.launchedAt || act.isLaunched || (act.responses && act.responses.length > 0);
              if (isLaunched) {
                launchedActivities++;
                totalResponses += act.responses?.length || act.responseCount || 0;
              }
            });
          });

          const participantCount = foundSession.participantCount || joinedParticipants.length || 1;
          // Engagement = (total responses / (launched activities * participants)) * 100
          const totalPossibleResponses = launchedActivities * participantCount;
          const engagement = totalPossibleResponses > 0
            ? Math.min(100, Math.round((totalResponses / totalPossibleResponses) * 100))
            : 0;
          setEngagementScore(engagement);

          // Set overall feedback state — also seed the responses array from
          // what's already persisted on the session doc so the sidebar count
          // (and the "X responses" stat in the panel) reflect submissions
          // made before the trainer opened/refreshed this page. Without this,
          // `overallFeedbackResponses` only ever picks up live socket events,
          // so a trainer landing on the page mid-feedback saw 0 responses
          // even when participants had already submitted.
          setOverallFeedbackEnabled(foundSession.overallFeedback?.enabled || false);
          setOverallFeedbackLaunched(foundSession.overallFeedback?.launched || false);
          setOverallFeedbackResponses(foundSession.overallFeedback?.responses || []);

          // Check for active activity and load its existing responses
          let activeActivity = null;
          foundSession.subSessions?.forEach(ss => {
            ss.activities?.forEach(act => {
              if (act.status === 'active') {
                activeActivity = act;
              }
            });
          });

          if (activeActivity) {
            console.log('[SessionControl] Found active activity:', activeActivity.title);
            setCurrentActivity(activeActivity);
            // Load existing responses from the activity
            const existingResponses = activeActivity.responses || [];
            console.log('[SessionControl] Loading existing responses:', existingResponses.length);
            setActivityResponses(existingResponses.map(r => ({
              ...r.response,
              participantId: r.participant?.toString() || r.participantId,
              participantName: r.participantName,
              timestamp: r.submittedAt,
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    // Get real-time session data from context
    const liveSession = getSession(sessionId);
    if (liveSession) {
      setParticipants(liveSession.participantCount || 0);
      if (liveSession.currentActivity) {
        setCurrentActivity(liveSession.currentActivity);
        setActivityResponses(liveSession.responses || []);
      }

      // Load participant list from context or API
      if (liveSession.participants) {
        setParticipantList(liveSession.participants);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // Only re-fetch when sessionId changes, not when getSession reference changes

  // Calculate elapsed time
  useEffect(() => {
    if (!sessionStartTime) return;

    const calculateElapsed = () => {
      const now = new Date();
      const diff = Math.floor((now - sessionStartTime) / 1000); // seconds

      const formatHMS = (secs) => {
        const totalHours = Math.floor(secs / 3600);
        const minutes = Math.floor((secs % 3600) / 60);
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        if (days > 0) {
          let out = `${days}d`;
          if (hours > 0) out += ` ${hours}h`;
          if (minutes > 0) out += ` ${minutes}m`;
          return out;
        }
        if (totalHours > 0) return `${totalHours}h ${minutes}m`;
        return `${minutes}m`;
      };

      // If session start time is in the future, show "Starts in Xm"
      if (diff < 0) {
        setElapsedTime(`Starts in ${formatHMS(Math.abs(diff))}`);
        return;
      }

      setElapsedTime(formatHMS(diff));
    };

    calculateElapsed();
    const interval = setInterval(calculateElapsed, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [sessionStartTime]);

  // Early start countdown timer - updates every second
  // Uses pseudo-UTC comparison (same timezone conversion as initial check)
  useEffect(() => {
    if (!showEarlyStartWarning || !scheduledStartTime) return;

    const updateCountdown = () => {
      // Convert current time to session's timezone as pseudo-UTC
      const now = new Date();
      let pseudoUtcNow;
      try {
        const localTimeStr = now.toLocaleString('sv-SE', { timeZone: sessionTimezone });
        pseudoUtcNow = new Date(localTimeStr.replace(' ', 'T') + '.000Z');
      } catch {
        pseudoUtcNow = now;
      }

      const diffMs = scheduledStartTime - pseudoUtcNow;

      if (diffMs <= 0) {
        // Time has arrived - close the warning and allow starting
        setShowEarlyStartWarning(false);
        setEarlyStartMinutes(0);
        setEarlyStartSeconds(0);
        return;
      }

      const minutes = Math.floor(diffMs / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      setEarlyStartMinutes(minutes);
      setEarlyStartSeconds(seconds);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [showEarlyStartWarning, scheduledStartTime, sessionTimezone]);

  // Break countdown timer
  useEffect(() => {
    if (!isOnBreak || !breakEndTime) return;

    const updateBreakTime = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((breakEndTime - now) / 1000));
      setBreakTimeRemaining(remaining);
    };

    updateBreakTime();
    const interval = setInterval(updateBreakTime, 1000);

    return () => clearInterval(interval);
  }, [isOnBreak, breakEndTime]);

  // Listen for real-time updates via WebSocket
  // Use socket.socket to get the actual Socket.io instance
  const socketInstance = socket?.socket;
  const isSocketConnected = socket?.isConnected;

  /**
   * Re-read the participant list from the server.
   *
   * The join/leave socket events carry only a name and a count, never the participant
   * record, so the list can't be built from them: anyone who joined after this page
   * loaded raised the "total" but never appeared in the list (and "online" stayed 0)
   * until a manual refresh. The server's list has names, emails and status. Debounced
   * so the burst of joins at the start of a session costs one request, not forty.
   */
  const participantRefreshTimer = useRef(null);
  const refreshParticipantList = useCallback(() => {
    clearTimeout(participantRefreshTimer.current);
    participantRefreshTimer.current = setTimeout(async () => {
      try {
        const response = await trainerService.getSession(sessionId);
        const joined = response?.data?.participants?.filter((p) => p.status === 'joined');
        if (joined) setParticipantList(joined);
      } catch {
        /* keep the list we have; the next join or leave tries again */
      }
    }, 800);
  }, [sessionId]);

  useEffect(() => () => clearTimeout(participantRefreshTimer.current), []);

  useEffect(() => {
    if (!socketInstance || !isSocketConnected || !sessionId) return;

    // Participant joined (backend emits 'participant-joined' with dash)
    const handleParticipantJoined = (data) => {
      console.log('[SessionControl] Participant joined:', data);
      // Use the accurate participant count from the server
      if (typeof data.participantCount === 'number') {
        setParticipants(data.participantCount);
      } else {
        // Fallback: fetch latest count from API
        setParticipants((prev) => prev + 1);
      }
      // The event has no participant record (see refreshParticipantList), so re-read
      // the list rather than append to it.
      refreshParticipantList();
    };

    // Participant left (backend emits 'participant-left' with dash)
    const handleParticipantLeft = (data) => {
      console.log('[SessionControl] Participant left:', data);
      // Use the accurate participant count from the server
      if (typeof data.participantCount === 'number') {
        setParticipants(data.participantCount);
      } else {
        setParticipants((prev) => Math.max(0, prev - 1));
      }
      refreshParticipantList();
    };

    // Activity launched (from another tab/window). This handler stays in
    // sync with launches that originated elsewhere — but it should only
    // ACCEPT a new activity when the local state agrees that no other
    // activity is currently live. If we already have a live activity and a
    // DIFFERENT one arrives, that means the guard in handleLaunchActivity
    // was bypassed somewhere (race, manual API call, server-side launch
    // without socket emit, etc.) — log loudly so the anomaly is visible,
    // and surface a toast to the trainer so they're not silently swapped
    // onto a different activity mid-flight.
    const handleActivityLaunched = (activity) => {
      const incomingId = activity?._id || activity?.id;
      setCurrentActivity((prev) => {
        const prevId = prev?._id || prev?.id;
        const prevIsLive = prev && prev.status === 'active';
        if (prevIsLive && incomingId && prevId !== incomingId) {
          console.warn(
            '[SessionControl] Activity launch arrived while another is still active:',
            { current: prevId, incoming: incomingId }
          );
          try {
            toast.error(
              `Another launch arrived while "${prev.title || prev.name || 'an activity'}" was still live. Check the activity list — state may be out of sync.`,
              { duration: 6000 }
            );
          } catch (_) { /* toast may not be available in some contexts */ }
        }
        return activity;
      });
      console.log('[SessionControl] Activity launched:', activity);
      setActivityResponses([]);
    };

    // Activity closed (from another tab/window or when activity ends)
    const handleActivityClosed = (data) => {
      console.log('[SessionControl] Activity closed:', data);
      // Update the activity's response count in session state and sync currentSubSession
      if (data?.activityId) {
        setSession(prev => {
          if (!prev?.subSessions) return prev;
          const updatedSubSessions = prev.subSessions.map(sub => ({
            ...sub,
            activities: (sub.activities || []).map(act =>
              (act._id === data.activityId || act.id === data.activityId)
                ? { ...act, responseCount: data.responseCount || 0, status: 'completed' }
                : act
            )
          }));

          // Find the subsession that contains the closed activity and keep it as current
          const subsessionWithActivity = updatedSubSessions.find(sub =>
            (sub.activities || []).some(act =>
              (act._id === data.activityId || act.id === data.activityId)
            )
          );

          if (subsessionWithActivity) {
            setCurrentSubSession(subsessionWithActivity);
          } else {
            // Fallback: keep the current subsession by finding it in updated list
            setCurrentSubSession(currSub => {
              if (!currSub) return currSub;
              const currentSubId = currSub._id || currSub.id;
              const updatedSub = updatedSubSessions.find(sub => (sub._id || sub.id) === currentSubId);
              return updatedSub || currSub;
            });
          }

          return { ...prev, subSessions: updatedSubSessions };
        });
      }
      setCurrentActivity(null);
      setActivityResponses([]);
    };

    // Response received from participant (backend emits 'response-received' with dash)
    const handleResponseReceived = (data) => {
      console.log('[SessionControl] Response received:', data);
      setActivityResponses((prev) => {
        // Merge response data with participantId for proper deduplication
        const responseWithParticipant = {
          ...data.response,
          participantId: data.participantId,
          timestamp: data.timestamp,
        };
        const existingIndex = prev.findIndex((r) => r.participantId === data.participantId);
        let newResponses;
        if (existingIndex >= 0) {
          newResponses = [...prev];
          newResponses[existingIndex] = responseWithParticipant;
        } else {
          newResponses = [...prev, responseWithParticipant];
        }

        // Broadcast updated results to participants for polls
        if (data.response?.type === 'poll' && socketInstance && currentActivity) {
          const options = currentActivity.config?.options || currentActivity.options || [];
          const votes = new Array(options.length).fill(0);
          newResponses.forEach(r => {
            if (r.type === 'poll' && typeof r.selectedOption === 'number') {
              votes[r.selectedOption] = (votes[r.selectedOption] || 0) + 1;
            }
          });

          socketInstance.emit('update-results', {
            sessionId,
            activityId: currentActivity._id || currentActivity.id,
            results: {
              options: options.map(opt => opt.text || opt),
              votes,
              totalVotes: newResponses.filter(r => r.type === 'poll').length,
              totalParticipants: participants,
              participantsVoted: newResponses.filter(r => r.type === 'poll').length,
            }
          });
        }

        // Broadcast updated results to participants for quizzes
        if (data.response?.type === 'quiz' && socketInstance && currentActivity) {
          const quizResponses = newResponses.filter(r => r.type === 'quiz');
          const totalScore = quizResponses.reduce((sum, r) => sum + (r.score || 0), 0);
          const averageScore = quizResponses.length > 0 ? Math.round(totalScore / quizResponses.length) : 0;

          socketInstance.emit('update-results', {
            sessionId,
            activityId: currentActivity._id || currentActivity.id,
            results: {
              type: 'quiz',
              totalSubmissions: quizResponses.length,
              averageScore,
              totalParticipants: participants,
              participantsSubmitted: quizResponses.length,
            }
          });
        }

        // Broadcast updated results to participants for word clouds
        if ((data.response?.type === 'wordCloud' || data.response?.type === 'wordcloud') && socketInstance && currentActivity) {
          const wordCloudResponses = newResponses.filter(r => r.type === 'wordCloud' || r.type === 'wordcloud');
          const wordCounts = {};

          wordCloudResponses.forEach(r => {
            const words = r.words || r.response?.words || [];
            words.forEach(word => {
              const normalizedWord = word.toLowerCase().trim();
              if (normalizedWord) {
                wordCounts[normalizedWord] = (wordCounts[normalizedWord] || 0) + 1;
              }
            });
          });

          // Convert to array sorted by count (descending)
          const aggregatedWords = Object.entries(wordCounts)
            .map(([word, count]) => ({ word, count }))
            .sort((a, b) => b.count - a.count);

          socketInstance.emit('update-results', {
            sessionId,
            activityId: currentActivity._id || currentActivity.id,
            results: {
              type: 'wordCloud',
              words: aggregatedWords,
              totalSubmissions: wordCloudResponses.length,
              totalParticipants: participants,
              participantsSubmitted: wordCloudResponses.length,
            }
          });
        }

        return newResponses;
      });
    };

    // Handle overall feedback response
    const handleOverallFeedbackResponse = (data) => {
      console.log('[SessionControl] Overall feedback response received:', data);
      if (data.latestResponse) {
        setOverallFeedbackResponses(prev => [...prev, data.latestResponse]);
      }
    };

    // Timer state changed (pause / resume / extend / stop). Merge the new values
    // into currentActivity so the countdown chip + controls reflect the update
    // immediately for the trainer who clicked as well as any other trainer tabs.
    const handleTimerUpdated = (data) => {
      if (!data?.activityId) return;
      setCurrentActivity(prev => {
        const currentId = prev?._id?.toString() || prev?.id?.toString();
        if (!prev || currentId !== data.activityId.toString()) return prev;
        return {
          ...prev,
          launchedAt: data.launchedAt || prev.launchedAt,
          timeLimit: (typeof data.timeLimit === 'number') ? data.timeLimit : prev.timeLimit,
          timerEnabled: (typeof data.timerEnabled === 'boolean') ? data.timerEnabled : prev.timerEnabled,
          timerPausedAt: data.timerPausedAt || null,
        };
      });
    };

    // Subscribe to events using the actual Socket.io instance
    socketInstance.on('participant-joined', handleParticipantJoined);
    socketInstance.on('participant-left', handleParticipantLeft);
    socketInstance.on('activity:launched', handleActivityLaunched);
    socketInstance.on('activity:closed', handleActivityClosed);
    socketInstance.on('activity:timer-updated', handleTimerUpdated);
    socketInstance.on('response-received', handleResponseReceived);
    socketInstance.on('overallFeedback:response', handleOverallFeedbackResponse);

    console.log('[SessionControl] WebSocket handlers registered for session:', sessionId);

    // Cleanup
    return () => {
      socketInstance.off('participant-joined', handleParticipantJoined);
      socketInstance.off('participant-left', handleParticipantLeft);
      socketInstance.off('activity:launched', handleActivityLaunched);
      socketInstance.off('activity:closed', handleActivityClosed);
      socketInstance.off('activity:timer-updated', handleTimerUpdated);
      socketInstance.off('response-received', handleResponseReceived);
      socketInstance.off('overallFeedback:response', handleOverallFeedbackResponse);
      console.log('[SessionControl] WebSocket handlers cleaned up');
    };
  }, [socketInstance, isSocketConnected, sessionId, currentActivity, participants, refreshParticipantList]);

  const handleLaunchActivity = async (activity, skipConfirmation = false) => {
    console.log('[SessionControl] handleLaunchActivity called with:', activity);

    // Guard: don't allow launching a second activity while one is still
    // active in this subsession. Trainer must end the current activity
    // first — otherwise participants get two live activities at once and
    // responses end up split across both.
    const isStillActive = currentActivity
      && currentActivity.status === 'active'
      && (currentActivity._id || currentActivity.id) !== (activity._id || activity.id);
    if (isStillActive) {
      const liveName = currentActivity.title || currentActivity.name || 'the current activity';
      setActivityActiveAlert({
        title: 'Stop the launched activity first',
        message: `"${liveName}" is still live. Please stop it before launching another activity — running two activities at once will split participant responses.`,
      });
      return;
    }

    // Check if activity has already been launched (has responses)
    const hasResponses = (activity.responses && activity.responses.length > 0) ||
                        (activity.responseCount && activity.responseCount > 0);

    if (hasResponses && !skipConfirmation) {
      // Show confirmation dialog
      setPendingRelaunchActivity(activity);
      setShowRelaunchConfirm(true);
      return;
    }

    // Find the subsession that contains this activity (no state change yet —
    // we hold off on every optimistic update until the backend confirms the
    // launch is allowed; otherwise a 409 would still flip local state and
    // broadcast to participants).
    const launchActivityId = activity._id || activity.id;
    const subsessionWithActivity = session?.subSessions?.find(sub =>
      (sub.activities || []).some(act =>
        (act._id === launchActivityId || act.id === launchActivityId)
      )
    );

    // Create serializable version of activity (remove React elements like icons)
    // Get timeLimit from activity or config (database default is 300 seconds = 5 minutes)
    const activityTimeLimit = activity.timeLimit || activity.duration || activity.config?.timeLimit || 300;
    const serializableActivity = {
      id: activity._id || activity.id,
      _id: activity._id,
      type: activity.type,
      title: activity.title || activity.name,
      name: activity.name || activity.title,
      question: activity.question || activity.config?.question,
      options: activity.options || activity.config?.options,
      config: activity.config,
      duration: activityTimeLimit,
      timeLimit: activityTimeLimit,
      isLive: true,
      status: 'active',
      totalParticipants: participants, // Current participant count for poll/activity displays
      participantCount: participants,
    };

    // Call API to persist activity status FIRST. Backend has the authoritative
    // "another activity is currently live" guard — when it responds 409 we
    // must NOT flip local state or broadcast `activity:launched` to participants
    // (the previous behaviour silently swallowed the error and ran the rest of
    // the flow anyway, so participants got two activities at once).
    const activityId = activity._id || activity.id;
    const isMongoId = activityId && /^[a-fA-F0-9]{24}$/.test(activityId);
    if (isMongoId) {
      try {
        await trainerService.launchActivity(sessionId, activityId);
        console.log('[SessionControl] Activity persisted to database:', activityId);
      } catch (error) {
        console.error('[SessionControl] Failed to persist activity:', error);
        const backendMessage = error?.message || '';
        const isAnotherLive = /another activity/i.test(backendMessage) || /currently live/i.test(backendMessage);
        if (isAnotherLive) {
          // Same alert as the local guard — but driven by the backend's truth
          // when local state hasn't caught up (e.g. trainer relaunched without
          // a refresh and the previous `currentActivity` got stale).
          setActivityActiveAlert({
            title: 'Stop the launched activity first',
            message: backendMessage || 'Another activity is still live. Please end it before launching a new one.',
          });
        } else {
          toast.error(backendMessage || 'Failed to launch activity. Please try again.');
        }
        return;
      }
    }

    // Backend accepted the launch — safe to flip local state and broadcast.
    if (subsessionWithActivity) {
      setCurrentSubSession(subsessionWithActivity);
    }
    setCurrentActivity(serializableActivity);
    // Preload responses from previous run on relaunch so the sidebar / right
    // panel keep showing the cumulative count instead of resetting to 0. The
    // response-received handler dedupes by participantId, so a participant
    // re-submitting after relaunch overwrites their old answer rather than
    // double-counting.
    const previousResponses = (activity.responses || []).map((r) => ({
      ...(r.response || {}),
      participantId: r.participant?.toString() || r.participantId,
      participantName: r.participantName,
      timestamp: r.submittedAt || r.timestamp,
    }));
    setActivityResponses(previousResponses);
    setShowActivityModal(false);

    // Update session context (persists to localStorage)
    launchActivityInContext(sessionId, serializableActivity);

    // Emit event via WebSocket (broadcasts to participants)
    if (socket) {
      socket.emit('activity:launched', serializableActivity);
      console.log('[SessionControl] Activity launched via WebSocket:', serializableActivity);
    }

    // Real responses will come via WebSocket 'response-received' events
  };

  // Handle confirmed relaunch of activity
  const handleConfirmRelaunch = () => {
    if (pendingRelaunchActivity) {
      handleLaunchActivity(pendingRelaunchActivity, true); // Skip confirmation
    }
    setShowRelaunchConfirm(false);
    setPendingRelaunchActivity(null);
  };

  const handleCloseActivity = async () => {
    const activityId = currentActivity?._id || currentActivity?.id;
    const currentResponseCount = activityResponses.length;

    // Update the activity's response count in our local activities list
    if (activityId) {
      // Update in subSessions and also update currentSubSession to reflect the change
      setSession(prev => {
        if (!prev?.subSessions) return prev;
        const updatedSubSessions = prev.subSessions.map(sub => ({
          ...sub,
          activities: (sub.activities || []).map(act =>
            (act._id === activityId || act.id === activityId)
              ? { ...act, responseCount: currentResponseCount, status: 'completed' }
              : act
          )
        }));

        // Find the subsession that contains the closed activity and keep it as current
        // This ensures we stay on the correct subsession after closing an activity
        const subsessionWithActivity = updatedSubSessions.find(sub =>
          (sub.activities || []).some(act =>
            (act._id === activityId || act.id === activityId)
          )
        );

        if (subsessionWithActivity) {
          setCurrentSubSession(subsessionWithActivity);
        } else {
          // Fallback: keep the current subsession by finding it in updated list
          setCurrentSubSession(currSub => {
            if (!currSub) return currSub;
            const currentSubId = currSub._id || currSub.id;
            const updatedSub = updatedSubSessions.find(sub => (sub._id || sub.id) === currentSubId);
            return updatedSub || currSub;
          });
        }

        return { ...prev, subSessions: updatedSubSessions };
      });
    }

    // Call API to persist activity closed status with response count
    if (activityId && !activityId.startsWith('act-')) {
      try {
        await trainerService.closeActivity(sessionId, activityId, currentResponseCount);
        console.log('[SessionControl] Activity closed in database:', activityId, 'Responses:', currentResponseCount);
      } catch (error) {
        console.error('[SessionControl] Failed to close activity in database:', error);
      }
    }

    // Update local state
    setCurrentActivity(null);
    setActivityResponses([]);
    setShowProjection(false);

    // Update session context
    closeActivityInContext(sessionId);

    // Emit event via WebSocket with response count (only if there was an activity)
    if (socket && activityId) {
      socket.emit('activity:closed', { activityId, responseCount: currentResponseCount });
    }
  };

  const handleToggleProjection = () => {
    const newState = !showProjection;
    setShowProjection(newState);

    // Emit projection state change
    if (socket) {
      socket.emit('projection:toggled', { showProjection: newState });
    }
  };

  const handlePauseResume = async () => {
    try {
      if (isPaused) {
        // Resume session (launch again)
        const response = await trainerService.launchSession(sessionId);
        if (response.success) {
          setIsPaused(false);
          setSession(response.data);
          if (socket) {
            socket.emit('session:resumed', { sessionId });
          }
        }
      } else {
        // Pause session
        const response = await trainerService.pauseSession(sessionId);
        if (response.success) {
          setIsPaused(true);
          setSession(response.data);
          if (socket) {
            socket.emit('session:paused', { sessionId });
          }
        }
      }
    } catch (error) {
      console.error('Error toggling pause state:', error);
    }
  };

  const handleEndSession = async (options = {}) => {
    const { reason, sendFeedbackForm, skipFeedbackCheck } = options;
    console.log('Ending session with options:', { reason, sendFeedbackForm, skipFeedbackCheck });

    // Block ending the session while an activity is still live — late
    // responses would be lost and per-activity reporting becomes unreliable.
    if (currentActivity && currentActivity.status === 'active') {
      const liveName = currentActivity.title || currentActivity.name || 'the current activity';
      setActivityActiveAlert({
        title: 'Stop the launched activity first',
        message: `"${liveName}" is still live. Please stop it before ending the session.`,
      });
      return;
    }

    // Check if overall feedback is enabled but not collected - warn user
    // Use session data directly to ensure we have the latest state
    const feedbackEnabled = session?.overallFeedback?.enabled !== false;
    const feedbackLaunched = session?.overallFeedback?.launched || overallFeedbackLaunched;
    // Check if any feedback has been collected (either from session data or local state)
    const feedbackCollected = (session?.overallFeedback?.responses?.length > 0) || (overallFeedbackResponses.length > 0);

    // Only warn if feedback is enabled, was launched, but no responses were collected
    if (feedbackEnabled && !feedbackCollected && !skipFeedbackCheck) {
      setPendingEndSessionOptions(options);
      setShowEndSessionWarning(true);
      return;
    }

    // Close any active activities
    if (currentActivity) {
      handleCloseActivity();
    }

    try {
      // Call API to end session
      const response = await trainerService.endSession(sessionId);

      if (response.success) {
        // Update local session state
        setSession(response.data);

        // Notify participants via WebSocket
        if (socket) {
          socket.emit('session:ended', { sessionId, reason });
        }

        setEndSessionMessage(sendFeedbackForm ? 'Participants will receive feedback forms.' : 'Session ended successfully.');
        setShowEndSessionModal(true);
      } else {
        console.error('Failed to end session:', response.error);
        setEndSessionMessage('Failed to end session. Please try again.');
        setShowEndSessionModal(true);
      }
    } catch (error) {
      console.error('Error ending session:', error);
      setEndSessionMessage('An error occurred while ending the session.');
      setShowEndSessionModal(true);
    }
  };

  // Handle end session after warning confirmation
  const handleEndSessionAnyway = () => {
    setShowEndSessionWarning(false);
    handleEndSession({ ...pendingEndSessionOptions, skipFeedbackCheck: true });
  };

  // Handle launching overall feedback from end session warning
  const handleLaunchFeedbackFromWarning = async () => {
    setShowEndSessionWarning(false);
    // Directly launch overall feedback instead of opening another modal
    await handleLaunchOverallFeedback();
  };

  const handleEndSubsession = async () => {
    console.log('Ending subsession:', currentSubSession?.name);

    // Guard: don't move to the next subsession while an activity is still
    // live — trainer must explicitly end the activity first. Without this,
    // late responses get attributed to whichever subsession is current,
    // making per-subsession reporting unreliable.
    if (currentActivity && currentActivity.status === 'active') {
      const liveName = currentActivity.title || currentActivity.name || 'the current activity';
      setShowEndSubsessionModal(false);
      setActivityActiveAlert({
        title: 'Stop the launched activity first',
        message: `"${liveName}" is still live in this subsession. Please stop it before moving to the next subsession.`,
      });
      return;
    }

    // Get subsession id for comparison (handles both _id and id)
    const getSubSessionId = (s) => s?._id || s?.id;
    const currentId = getSubSessionId(currentSubSession);
    const currentIndex = subSessions.findIndex(s => getSubSessionId(s) === currentId);

    // Get break time from current subsession configuration
    // Note: Model stores as 'breakAfter', but also check legacy field names for compatibility
    const breakTime = currentSubSession?.breakAfter || currentSubSession?.breakTime || currentSubSession?.breakDuration || 0;

    // Notify participants via WebSocket that subsession has ended
    if (socket && currentSubSession) {
      socket.emit('subsession:ended', {
        sessionId,
        subsessionId: currentId,
        name: currentSubSession.name,
        breakTime: breakTime, // Break time in minutes
        hasBreak: breakTime > 0,
      });
      console.log('[SessionControl] Subsession ended event emitted:', currentSubSession.name, 'Break time:', breakTime);
    }

    setShowEndSubsessionModal(false);

    // If there's a break, start break timer and show break modal
    if (breakTime > 0 && currentIndex < subSessions.length - 1) {
      const breakDurationMs = breakTime * 60 * 1000; // Convert minutes to ms
      setBreakEndTime(Date.now() + breakDurationMs);
      setBreakTimeRemaining(breakTime * 60); // In seconds
      setCurrentBreakInfo({
        name: currentSubSession?.name || 'Current Section',
        breakTime: breakTime,
      });
      setIsOnBreak(true);
      setShowBreakModal(true);
      // Don't move to next subsession yet - will move when break ends or trainer skips
    } else if (currentIndex < subSessions.length - 1) {
      // No break, immediately move to next subsession
      const nextSubSession = subSessions[currentIndex + 1];
      setCurrentSubSession(nextSubSession);

      // Notify participants that new subsession has started
      if (socket && nextSubSession) {
        socket.emit('subsession:started', {
          sessionId,
          subsessionId: getSubSessionId(nextSubSession),
          name: nextSubSession.name,
          number: currentIndex + 2, // 1-indexed
        });
        console.log('[SessionControl] Subsession started event emitted:', nextSubSession.name);
      }
    } else if (currentIndex === subSessions.length - 1) {
      // This was the last subsession
      // Check if overall session feedback is enabled and not yet launched
      if (overallFeedbackEnabled && !overallFeedbackLaunched) {
        setOverallFeedbackTrigger('end');
        setShowOverallFeedbackModal(true);
      }
    }

    // Persist subsession completion to database
    try {
      await trainerService.completeSubsession(sessionId, currentId);
    } catch (err) {
      console.error('[SessionControl] Failed to persist subsession completion:', err);
    }
  };

  // Launch overall session feedback
  const handleLaunchOverallFeedback = async () => {
    // Block launching feedback while an activity is still live — feedback
    // and activity responses would compete for participant attention.
    if (currentActivity && currentActivity.status === 'active') {
      const liveName = currentActivity.title || currentActivity.name || 'the current activity';
      setShowOverallFeedbackModal(false);
      setActivityActiveAlert({
        title: 'Stop the launched activity first',
        message: `"${liveName}" is still live. Please stop it before launching overall feedback.`,
      });
      return;
    }

    console.log('[SessionControl] Launching overall feedback for session:', sessionId);
    try {
      const response = await trainerService.launchOverallFeedback(sessionId);
      console.log('[SessionControl] Launch response:', response);
      if (response.success) {
        setOverallFeedbackLaunched(true);
        setShowOverallFeedbackModal(false);
        setOverallFeedbackResponses([]); // Reset responses
        // Socket event is emitted by the backend
        console.log('[SessionControl] Overall feedback launched successfully');
      } else {
        console.error('Failed to launch overall feedback:', response.error);
        alert('Failed to launch overall feedback: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error launching overall feedback:', error);
      alert('Error launching overall feedback: ' + (error.message || error.response?.data?.error || 'Unknown error'));
    }
  };

  // Skip overall feedback and proceed
  const handleSkipOverallFeedback = () => {
    setShowOverallFeedbackModal(false);
  };

  // End/Stop overall feedback
  const handleEndOverallFeedback = async () => {
    try {
      const response = await trainerService.endOverallFeedback(sessionId);
      if (response.success) {
        setOverallFeedbackLaunched(false);
        console.log('[SessionControl] Overall feedback ended successfully');
      } else {
        console.error('Failed to end overall feedback:', response.error);
      }
    } catch (error) {
      console.error('Error ending overall feedback:', error);
    }
  };

  // Skip break and move to next subsession immediately
  const handleSkipBreak = () => {
    setIsOnBreak(false);
    setBreakEndTime(null);
    setBreakTimeRemaining(0);
    setShowBreakModal(false);

    // Move to next subsession
    const getSubSessionId = (s) => s?._id || s?.id;
    const currentId = getSubSessionId(currentSubSession);
    const currentIndex = subSessions.findIndex(s => getSubSessionId(s) === currentId);
    if (currentIndex < subSessions.length - 1) {
      const nextSubSession = subSessions[currentIndex + 1];
      setCurrentSubSession(nextSubSession);

      // Notify participants that new subsession has started
      if (socket && nextSubSession) {
        socket.emit('subsession:started', {
          sessionId,
          subsessionId: getSubSessionId(nextSubSession),
          name: nextSubSession.name,
          number: currentIndex + 2, // 1-indexed
        });
        console.log('[SessionControl] Subsession started event emitted:', nextSubSession.name);
      }
    }
  };

  // Move to next subsession when break ends
  const handleBreakEnded = () => {
    setIsOnBreak(false);
    setBreakEndTime(null);
    setBreakTimeRemaining(0);
    setShowBreakModal(false);

    // Move to next subsession
    const getSubSessionId = (s) => s?._id || s?.id;
    const currentId = getSubSessionId(currentSubSession);
    const currentIndex = subSessions.findIndex(s => getSubSessionId(s) === currentId);
    if (currentIndex < subSessions.length - 1) {
      const nextSubSession = subSessions[currentIndex + 1];
      setCurrentSubSession(nextSubSession);

      // Notify participants that new subsession has started
      if (socket && nextSubSession) {
        socket.emit('subsession:started', {
          sessionId,
          subsessionId: getSubSessionId(nextSubSession),
          name: nextSubSession.name,
          number: currentIndex + 2, // 1-indexed
        });
        console.log('[SessionControl] Subsession started event emitted:', nextSubSession.name);
      }
    }
  };

  // Delete activity handler
  const handleDeleteActivity = async (activity) => {
    if (!activity) return;
    setPendingDeleteActivity(activity);
    setShowDeleteActivityConfirm(true);
  };

  const confirmDeleteActivity = async () => {
    if (!pendingDeleteActivity || !currentSubSession) return;

    const activityId = pendingDeleteActivity._id || pendingDeleteActivity.id;
    const subSessionId = currentSubSession._id || currentSubSession.id;

    try {
      setDeletingActivity(true);
      const response = await trainerService.deleteActivity(sessionId, subSessionId, activityId);

      if (response.success) {
        // Update local state to remove the activity
        setSession(prev => {
          if (!prev?.subSessions) return prev;
          const updatedSubSessions = prev.subSessions.map(sub => {
            const subId = sub._id || sub.id;
            if (subId === subSessionId) {
              return {
                ...sub,
                activities: (sub.activities || []).filter(act =>
                  (act._id || act.id) !== activityId
                )
              };
            }
            return sub;
          });
          return { ...prev, subSessions: updatedSubSessions };
        });

        // Update currentSubSession state
        setCurrentSubSession(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            activities: (prev.activities || []).filter(act =>
              (act._id || act.id) !== activityId
            )
          };
        });

        // If the deleted activity was the current one, clear it
        const currentId = currentActivity?._id || currentActivity?.id;
        if (currentId === activityId) {
          setCurrentActivity(null);
          setActivityResponses([]);
        }

        console.log('[SessionControl] Activity deleted successfully:', activityId);
      } else {
        console.error('Failed to delete activity:', response.error);
        alert('Failed to delete activity: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Error deleting activity: ' + (error.message || error.response?.data?.error || 'Unknown error'));
    } finally {
      setDeletingActivity(false);
      setShowDeleteActivityConfirm(false);
      setPendingDeleteActivity(null);
    }
  };

  const handleExportData = async (format) => {
    try {
      // For PDF, use the professional backend-generated report
      if (format.toLowerCase() === 'pdf') {
        await exportService.exportSessionAnalyticsReportPDF(sessionId, session?.code);
        console.log('Session report PDF exported');
        return;
      }

      // For Excel/JSON, use client-side export with live session data
      const allActivities = currentSubSession?.activities || [];
      // Calculate real session duration from start time
      const durationMinutes = sessionStartTime
        ? Math.round((Date.now() - sessionStartTime.getTime()) / 60000)
        : (session.duration || 0);

      const sessionData = {
        session: {
          ...session,
          code: session.code || sessionId,
          date: Date.now(),
          duration: durationMinutes,
        },
        activities: allActivities.map((act) => ({
          ...act,
          completed: act.status === 'completed' || act.id === currentActivity?.id,
        })),
        participants: participantList,
        responses: activityResponses,
      };

      exportSessionData(sessionData, format);
      console.log(`Session data exported as ${format}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export session data. Please try again.');
    }
  };

  const handleRemoveParticipant = (participant) => {
    if (window.confirm(`Remove ${participant.name} from the session?`)) {
      // Records from the server carry `_id`, not `id`; comparing `id` alone matched
      // undefined === undefined and emptied the whole list.
      const rowId = (p) => p.id || p._id;
      setParticipantList((prev) => prev.filter((p) => rowId(p) !== rowId(participant)));
      setParticipants((prev) => Math.max(0, prev - 1));
      console.log('Participant removed:', participant);
      // Notify removed participant via WebSocket
      if (socket) {
        socket.emit('participant:removed', {
          sessionId,
          participantId: participant.id,
          participantName: participant.name,
        });
      }
    }
  };

  const handleSendMessage = (participant) => {
    if (participant === 'all') {
      alert('Notification sent to all participants!');
      console.log('Broadcast message to all participants');
    } else {
      alert(`Message sent to ${participant.name}!`);
      console.log('Direct message sent to:', participant);
    }
    // TODO: Implement messaging system
  };

  const handleEditActivity = (activity) => {
    // Block edits on live activities — matches backend guard at updateActivity.
    if (activity?.status === 'active') {
      alert('Cannot edit a live activity. Please end it first.');
      return;
    }
    setSelectedActivityForSettings(activity);
    setShowActivitySettings(true);
  };

  const handleUpdateActivitySettings = (settings) => {
    console.log('Updating activity settings:', settings);
    // TODO: Update activity configuration
    setShowActivitySettings(false);
  };

  const handleExportParticipants = () => {
    try {
      exportParticipantData(participantList, 'excel');
      console.log('Participant list exported');
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleLaunchWorkbook = async (workbook) => {
    setLaunchedWorkbook(workbook);
    setShowWorkbookModal(false);

    try {
      // Use API to persist the launched workbook (more reliable than socket)
      await trainerService.launchWorkbook(sessionId, workbook);
      console.log('Workbook launched via API:', workbook.name);
    } catch (error) {
      console.error('Error launching workbook via API:', error);
      // Fallback: emit via socket if API fails
      if (socket) {
        socket.emit('workbook:launched', workbook);
        console.log('Workbook launched via socket fallback:', workbook.name);
      }
    }
  };

  const handleStopWorkbook = async () => {
    setLaunchedWorkbook(null);

    try {
      // Use API to stop the workbook (more reliable than socket)
      await trainerService.stopWorkbook(sessionId);
      console.log('Workbook stopped via API');
    } catch (error) {
      console.error('Error stopping workbook via API:', error);
      // Fallback: emit via socket if API fails
      if (socket) {
        socket.emit('workbook:stopped', {});
        console.log('Workbook stopped via socket fallback');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Session Control">
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-neutral-600">Loading session...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!session) {
    return (
      <DashboardLayout title="Session Control">
        <div className="p-6">
          <p className="text-neutral-600">Session not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const subSessions = session.subSessions || [];
  const allActivities = currentSubSession?.activities || [];

  // Helper function to get subsession identifier (handles both _id and id)
  const getSubSessionId = (subSession) => subSession?._id || subSession?.id;

  // Helper function to find current subsession index
  const getCurrentSubSessionIndex = () => {
    if (!currentSubSession) return -1;
    const currentId = getSubSessionId(currentSubSession);
    return subSessions.findIndex(s => getSubSessionId(s) === currentId);
  };

  // Activity types with colorful icons from activityIcons utility
  const activityTypes = [
    {
      type: 'poll',
      name: 'Poll',
      description: 'Quick opinion polls',
      colorClass: getActivityTypeColor('poll'),
    },
    {
      type: 'quiz',
      name: 'Quiz',
      description: 'Test knowledge',
      colorClass: getActivityTypeColor('quiz'),
    },
    {
      type: 'wordcloud',
      name: 'Word Cloud',
      description: 'Collect words',
      colorClass: getActivityTypeColor('wordcloud'),
    },
    {
      type: 'openquestion',
      name: 'Open Text',
      description: 'Free-text responses',
      colorClass: getActivityTypeColor('openquestion'),
    },
    {
      type: 'qa',
      name: 'Q&A',
      description: 'Audience questions',
      colorClass: getActivityTypeColor('qa'),
    },
    {
      type: 'challenges',
      name: 'Challenges',
      description: 'Collect challenges',
      colorClass: getActivityTypeColor('challenges'),
    },
    {
      type: 'actionplans',
      name: 'Action Plans',
      description: 'Collect action plans',
      colorClass: getActivityTypeColor('actionplans'),
    },
    {
      type: 'feedback',
      name: 'Feedback',
      description: 'Session feedback',
      colorClass: getActivityTypeColor('feedback'),
    },
  ];

  // Helper to render activity icon with color
  const renderActivityIcon = (type, size = 'md') => {
    return (
      <span className={`flex-shrink-0 rounded-md flex items-center justify-center ${getActivityTypeColor(type)}`}>
        {getActivityIcon(type, size)}
      </span>
    );
  };

  // Show ended session message
  if (sessionEnded && session) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-2">Session Has Ended</h2>
            <p className="text-neutral-600 mb-6">
              This session has been {session.status === 'completed' ? 'completed' : 'cancelled'} and can no longer be controlled.
              You can view the session report for analytics and participant responses.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate(`/trainer/sessions/${sessionId}`)}
                className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                View Session Details
              </button>
              <button
                onClick={() => navigate('/trainer/sessions')}
                className="w-full px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
              >
                Back to Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Early Start Warning Modal - Blocks starting before scheduled time */}
      <Modal
        isOpen={showEarlyStartWarning}
        onClose={() => navigate('/trainer/sessions')}
        title="Session Not Started Yet"
        size="sm"
      >
        <div className="text-center py-4">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Countdown Timer Display */}
          <div className="mb-4">
            <p className="text-sm text-neutral-500 mb-2">Session starts in</p>
            <div className="flex items-center justify-center gap-2">
              <div className="bg-neutral-900 text-white px-4 py-3 rounded-lg min-w-[70px]">
                <span className="text-2xl sm:text-3xl font-bold font-mono">{String(earlyStartMinutes).padStart(2, '0')}</span>
                <p className="text-xs text-neutral-400 mt-1">min</p>
              </div>
              <span className="text-2xl sm:text-3xl font-bold text-neutral-900">:</span>
              <div className="bg-neutral-900 text-white px-4 py-3 rounded-lg min-w-[70px]">
                <span className="text-2xl sm:text-3xl font-bold font-mono">{String(earlyStartSeconds).padStart(2, '0')}</span>
                <p className="text-xs text-neutral-400 mt-1">sec</p>
              </div>
            </div>
          </div>

          <p className="text-neutral-600 mb-6">
            Please wait until the scheduled time to start the session.
            The session will be available when the countdown reaches zero.
          </p>

          <button
            onClick={() => navigate('/trainer/sessions')}
            className="w-full px-4 py-2.5 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
          >
            Go Back to Sessions
          </button>
        </div>
      </Modal>

      {/* Clean Top Header - Slido Style */}
      <header className="h-14 border-b border-neutral-200 flex items-center justify-between px-2 sm:px-4 bg-white flex-shrink-0">
        {/* Left: Back & Session Info */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={() => navigate('/trainer/sessions')}
            className="p-1.5 sm:p-2 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <span className="text-sm sm:text-lg font-semibold text-neutral-900 truncate max-w-[100px] sm:max-w-none">{session.name}</span>
            <span className="text-xs sm:text-sm text-neutral-500 flex-shrink-0">#{session.code}</span>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Connection Status Indicator */}
          <div
            className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded-lg ${
              isSocketConnected
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
            title={isSocketConnected ? 'Connected to server' : 'Disconnected - activities may not work'}
          >
            <div className={`w-2 h-2 rounded-full ${
              isSocketConnected ? 'bg-green-500' : 'bg-red-500'
            }`} />
            <span className="text-xs font-medium hidden sm:inline">
              {isSocketConnected ? 'Live' : 'Offline'}
            </span>
          </div>

          {/* Participants Count */}
          <button
            onClick={() => setShowParticipantPanel(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors cursor-pointer"
            title="View Participants"
          >
            <svg className="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="text-xs sm:text-sm font-medium text-neutral-700">{participants}</span>
          </button>

          {/* QR Code */}
          <button
            onClick={() => setShowQRModal(true)}
            className="p-1.5 sm:p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            title="Show QR Code"
          >
            <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </button>

          {/* More Options */}
          <SessionControls
            sessionId={sessionId}
            onExportData={handleExportData}
            onEndSession={handleEndSession}
            onPauseSession={handlePauseResume}
            participantCount={participants}
            isMultiDay={session?.startTime && session?.endTime && new Date(session.startTime).toISOString().slice(0, 10) !== new Date(session.endTime).toISOString().slice(0, 10)}
            isPaused={isPaused}
          />
        </div>
      </header>

      {/* Mobile Sidebar Toggle Button */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="sm:hidden fixed bottom-4 left-4 z-40 w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-colors"
        title={sidebarCollapsed ? 'Show Activities' : 'Hide Activities'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {sidebarCollapsed
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          }
        </svg>
      </button>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Sidebar Overlay */}
        {!sidebarCollapsed && (
          <div
            className="sm:hidden fixed inset-0 bg-black/30 z-30"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* Left Sidebar - Slido Style */}
        <div className={`
          ${sidebarCollapsed ? 'w-0 sm:w-16 overflow-hidden' : 'w-[85vw] sm:w-80'}
          fixed sm:relative z-30 sm:z-auto top-14 sm:top-auto bottom-0 left-0
          border-r border-neutral-200 bg-white flex flex-col transition-all duration-300
        `}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-neutral-100">
            <div className="flex items-center justify-between mb-4">
              {!sidebarCollapsed && <h2 className="text-sm font-semibold text-neutral-900">My interactions</h2>}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1 hover:bg-neutral-100 rounded transition-colors"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg className={`w-4 h-4 text-neutral-400 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
            </div>

            {/* Add Button Group */}
            {sidebarCollapsed ? (
              <button
                onClick={() => setShowActivityModal(true)}
                className="w-full flex items-center justify-center p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                title="Add Activity"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add
                </button>
                {session.workbooks && session.workbooks.length > 0 && (
                  <button
                    onClick={() => setShowWorkbookModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 border border-neutral-300 hover:bg-neutral-50 rounded-lg text-sm font-medium text-neutral-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Workbook
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Active Workbook Indicator */}
          {launchedWorkbook && (
            <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} py-3 bg-blue-50 border-b border-blue-100`}>
              <div className={`flex ${sidebarCollapsed ? 'flex-col items-center gap-2' : 'items-center justify-between'}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                  {!sidebarCollapsed && <span className="text-xs font-medium text-blue-700 truncate max-w-[120px]">{launchedWorkbook.name}</span>}
                </div>
                <button
                  onClick={handleStopWorkbook}
                  className="flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex-shrink-0"
                  title="Stop Workbook"
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Sub-session selector */}
          {subSessions.length > 0 && !sidebarCollapsed && (
            <div className="px-4 py-3 border-b border-neutral-100">
              <div className="flex flex-col gap-2">
                <select
                  value={getSubSessionId(currentSubSession) || ''}
                  onChange={(e) => {
                    if (isOnBreak) return; // Prevent changing during break
                    const selected = subSessions.find(s => getSubSessionId(s) === e.target.value);
                    if (selected) setCurrentSubSession(selected);
                  }}
                  disabled={isOnBreak}
                  className={`w-full px-3 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isOnBreak
                      ? 'bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700'
                  }`}
                >
                  {subSessions.map((ss) => (
                    <option key={getSubSessionId(ss)} value={getSubSessionId(ss)}>
                      {ss.name} ({ss.activities?.length || 0} activities)
                    </option>
                  ))}
                </select>
                {subSessions.length > 1 && !isOnBreak && (
                  <button
                    onClick={() => {
                      // Block advancing while an activity is still live —
                      // late responses would otherwise be attributed to the
                      // wrong subsession.
                      if (currentActivity && currentActivity.status === 'active') {
                        const liveName = currentActivity.title || currentActivity.name || 'the current activity';
                        setActivityActiveAlert({
                          title: 'Stop the launched activity first',
                          message: `"${liveName}" is still live in this subsession. Please stop it before moving to the next subsession.`,
                        });
                        return;
                      }
                      setShowEndSubsessionModal(true);
                    }}
                    className="w-full px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    title="End current subsession and move to next"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                    End Subsession & Next
                  </button>
                )}
                {isOnBreak && (
                  <button
                    onClick={() => setShowBreakModal(true)}
                    className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    title="Break in progress"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Break: {Math.floor(breakTimeRemaining / 60)}:{(breakTimeRemaining % 60).toString().padStart(2, '0')}
                  </button>
                )}
                {/* Overall Feedback Button - shown when enabled but not launched */}
                {overallFeedbackEnabled && !overallFeedbackLaunched && (
                  <button
                    onClick={() => {
                      setOverallFeedbackTrigger('manual');
                      setShowOverallFeedbackModal(true);
                    }}
                    className="w-full px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    title="Launch overall session feedback"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Launch Overall Feedback
                  </button>
                )}
                {/* Overall Feedback Active Indicator with Stop Button */}
                {overallFeedbackLaunched && (
                  <div className="w-full px-3 py-2 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm font-medium">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Overall Feedback</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-200 px-2 py-0.5 rounded-full">{overallFeedbackResponses.length}</span>
                        <button
                          onClick={handleEndOverallFeedback}
                          className="flex items-center justify-center w-6 h-6 bg-red-500 hover:bg-red-600 rounded transition-colors"
                          title="Stop feedback collection"
                        >
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="6" width="12" height="12" rx="1" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Simulations — renders nothing unless the org has a playable one */}
          {!sidebarCollapsed && sessionId && (
            <div className="px-3 pb-2">
              <LaunchSimulationPanel sessionId={sessionId} />
            </div>
          )}

          {/* Paired micro simulations — renders nothing without access or a published topic */}
          {!sidebarCollapsed && sessionId && (
            <div className="px-3 pb-2">
              <LaunchMicroSimPanel sessionId={sessionId} />
            </div>
          )}

          {/* Activities List - Slido Style */}
          <div className="flex-1 overflow-y-auto">
            {/* Polls Section */}
            <div className="py-2">
              {!sidebarCollapsed && (
                <div className="px-4 py-2">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide flex items-center gap-1">
                    <span>Aktivities</span>
                    {currentSubSession?.name && (
                      <span className="normal-case overflow-hidden max-w-[120px]">
                        <span
                          className={`inline-block whitespace-nowrap ${currentSubSession.name.length > 15 ? 'animate-marquee hover:animate-none' : ''}`}
                          style={currentSubSession.name.length > 15 ? {
                            animation: 'marquee 8s linear infinite',
                          } : {}}
                        >
                          ({currentSubSession.name})
                        </span>
                      </span>
                    )}
                  </p>
                  <style>{`
                    @keyframes marquee {
                      0% { transform: translateX(0%); }
                      50% { transform: translateX(-50%); }
                      100% { transform: translateX(0%); }
                    }
                  `}</style>
                </div>
              )}

              {allActivities.length > 0 ? (
                <div className="space-y-1">
                  {allActivities.map((activity) => {
                    const activityType = activityTypes.find(t => t.type === activity.type) || {};
                    // Get activity identifier - prefer _id (from MongoDB), fallback to id
                    const activityIdentifier = activity._id || activity.id;
                    const currentActivityIdentifier = currentActivity?._id || currentActivity?.id;
                    // Only consider active if both have valid identifiers and they match
                    const isActive = !!(currentActivityIdentifier && activityIdentifier && currentActivityIdentifier === activityIdentifier);
                    // Show real response count: for active use activityResponses, otherwise use stored count from activity
                    const responseCount = isActive ? activityResponses.length : (activity.responseCount || activity.responses?.length || 0);

                    // Collapsed view
                    if (sidebarCollapsed) {
                      return (
                        <div
                          key={activity.id || activity._id}
                          className={`group relative mx-1 p-2 rounded-lg transition-all flex items-center justify-center ${
                            isActive
                              ? 'bg-primary-100 border-2 border-primary-500'
                              : 'hover:bg-neutral-100 border-2 border-transparent'
                          }`}
                          title={activity.name || activity.title || activity.question}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                            {getActivityIcon(activity.type, 'sm')}
                          </div>
                          {isActive && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
                          )}
                          {/* Play button overlay on hover - collapsed view */}
                          {!isActive && (
                            <button
                              onClick={() => {
                                console.log('[SessionControl] Collapsed play button clicked:', { activityIdentifier, activity });
                                handleLaunchActivity(activity);
                              }}
                              className="absolute inset-0 bg-green-500/90 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center"
                              title="Launch activity"
                            >
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={activity.id || activity._id}
                        className={`group mx-2 rounded-lg border-2 transition-all ${
                          isActive
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-transparent hover:bg-neutral-50'
                        }`}
                      >
                        <div className="p-3">
                          {/* Activity Title with Action Buttons */}
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-neutral-900 flex-1">
                              {activity.name || activity.title || (activity.question || activity.config?.question)?.substring(0, 40) + '...'}
                            </p>
                            {/* Action Buttons - visible on hover */}
                            {!isActive && (
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                {/* Play/Launch Button - Large like stop button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('[SessionControl] Launch button clicked:', { activityIdentifier, activity });
                                    handleLaunchActivity(activity);
                                  }}
                                  className="flex items-center justify-center w-8 h-8 bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                                  title="Launch activity"
                                >
                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </button>
                                {/* Remove Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteActivity(activity);
                                  }}
                                  className="flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                                  title="Delete activity"
                                >
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Activity Meta */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className={`w-5 h-5 rounded flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                {getActivityIcon(activity.type, 'sm')}
                              </span>
                              <span className="text-xs text-neutral-500 capitalize">{activity.type?.replace(/([A-Z])/g, ' $1').trim() || 'Activity'}</span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${responseCount > 0 ? 'bg-green-100 text-green-700 font-medium' : 'text-neutral-400'}`}>
                              {responseCount} {responseCount === 1 ? 'response' : 'responses'}
                            </span>
                          </div>

                          {/* Action Buttons - Show when active */}
                          {isActive && (
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-primary-200">
                              <div className="flex items-center gap-1">
                                {/* Visibility Toggle */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowProjection(!showProjection);
                                  }}
                                  className={`p-1.5 rounded hover:bg-primary-100 transition-colors ${showProjection ? 'text-primary-700' : 'text-neutral-500'}`}
                                  title={showProjection ? 'Hide results' : 'Show results'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>

                                {/* Lock Toggle */}
                                <button
                                  className="p-1.5 rounded hover:bg-primary-100 transition-colors text-neutral-500"
                                  title="Lock activity"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              </div>

                              {/* Stop Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCloseActivity();
                                }}
                                className="flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                                title="Stop activity"
                              >
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <rect x="6" y="6" width="12" height="12" rx="1" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`${sidebarCollapsed ? 'px-2 py-4' : 'px-4 py-8'} text-center`}>
                  <div className={`inline-flex items-center justify-center ${sidebarCollapsed ? 'w-8 h-8' : 'w-12 h-12'} bg-neutral-100 rounded-xl ${sidebarCollapsed ? '' : 'mb-3'}`}>
                    <svg className={`${sidebarCollapsed ? 'w-4 h-4' : 'w-6 h-6'} text-neutral-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <p className="text-sm text-neutral-500">No activities yet</p>
                      <p className="text-xs text-neutral-400 mt-1">Click Add to create one</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area - Slido Style */}
        <div className="flex-1 flex flex-col bg-neutral-50 overflow-hidden">
          {currentActivity ? (
            <>
              {/* Activity Header - Clean */}
              <div className="bg-white border-b border-neutral-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Activity Type Icon */}
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActivityTypeColor(currentActivity.type)}`}>
                      {getActivityIcon(currentActivity.type, 'md')}
                    </span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-neutral-500 capitalize">{currentActivity.type.replace('_', ' ')}</span>
                        <span className="text-xs text-neutral-300">•</span>
                        <span className="text-xs font-medium text-primary-600">{activityResponses.length} responses</span>
                        <ActivityCountdown activity={currentActivity} />
                      </div>
                    </div>
                  </div>
                  {/* Timer controls — only rendered when a timer is active on this activity. */}
                  <TimerControls activity={currentActivity} socket={socket?.socket} />
                </div>
              </div>

              {/* Activity Question Display */}
              <div className="bg-white mx-6 mt-6 rounded-xl border border-neutral-200 shadow-sm">
                <div className="p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-6">
                    {currentActivity.question || currentActivity.config?.question || currentActivity.title || currentActivity.name}
                  </h2>

                  {/* Toggle: Show real names or anonymous */}
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-neutral-100">
                    <button
                      onClick={() => setShowParticipantNames(!showParticipantNames)}
                      className="flex items-center gap-2 group"
                    >
                      <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${showParticipantNames ? 'bg-primary-500' : 'bg-neutral-300'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${showParticipantNames ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                    </button>
                    <p className="text-xs text-neutral-500">
                      {showParticipantNames ? 'Showing participant names' : 'Showing anonymous'}
                    </p>
                  </div>

                  {/* Results Display */}
                  <ActivityResultsViewer
                    activity={currentActivity}
                    responses={showParticipantNames
                      ? activityResponses
                      : activityResponses.map((r, i) => ({
                          ...r,
                          participantName: `Participant ${i + 1}`,
                          name: `Participant ${i + 1}`,
                        }))
                    }
                    totalParticipants={participants}
                    showProjection={showProjection}
                    onToggleProjection={handleToggleProjection}
                  />
                </div>
              </div>
            </>
          ) : overallFeedbackLaunched ? (
            /* Overall Feedback Results View */
            <>
              {/* Overall Feedback Header */}
              <div className="bg-white border-b border-neutral-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100 text-green-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </span>
                    <div>
                      <h2 className="text-lg font-semibold text-secondary-900">Overall Session Feedback</h2>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-neutral-500">Session Feedback</span>
                        <span className="text-xs text-neutral-300">•</span>
                        <span className="text-xs font-medium text-green-600">{overallFeedbackResponses.length} responses</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleEndOverallFeedback}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                    Stop Feedback
                  </button>
                </div>
              </div>

              {/* Overall Feedback Results */}
              <div className="flex-1 overflow-y-auto p-6">
                {overallFeedbackResponses.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-neutral-700 mb-2">Waiting for feedback...</h3>
                    <p className="text-sm text-neutral-500">Participants are being asked to rate the session</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Average Rating */}
                    {overallFeedbackResponses.length > 0 && (
                      <div className="bg-green-50 rounded-xl p-6 text-center mb-6">
                        <p className="text-sm text-green-600 font-medium mb-2">Average Rating</p>
                        <p className="text-4xl font-bold text-green-700">
                          {(overallFeedbackResponses.reduce((sum, r) => sum + (r.rating || 0), 0) / overallFeedbackResponses.length).toFixed(1)}
                        </p>
                        <p className="text-sm text-green-500 mt-1">out of 5</p>
                        <div className="flex justify-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={`text-2xl ${
                                star <= Math.round(overallFeedbackResponses.reduce((sum, r) => sum + (r.rating || 0), 0) / overallFeedbackResponses.length)
                                  ? 'text-yellow-400'
                                  : 'text-neutral-300'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Individual Responses */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-neutral-700">Recent Feedback</h4>
                      {overallFeedbackResponses.slice().reverse().map((response, index) => (
                        <div key={index} className="p-4 bg-white border border-neutral-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-green-600">
                                    {response.participantName ? response.participantName.charAt(0).toUpperCase() : 'A'}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-neutral-900">{response.participantName || 'Anonymous'}</span>
                                <div className="flex items-center gap-0.5">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-sm ${star <= (response.rating || 0) ? 'text-yellow-400' : 'text-neutral-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {response.comment && (
                                <p className="text-sm text-neutral-600 italic">"{response.comment}"</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-neutral-200 rounded-2xl mb-4">
                  <svg className="w-10 h-10 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-700 mb-2">No activity selected</h3>
                <p className="text-sm text-neutral-500 mb-4">Click on an activity from the sidebar to start</p>
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Create Activity
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation Bar - Clean Status Bar */}
      <div className="h-14 border-t border-neutral-200 bg-white flex items-center justify-between px-2 sm:px-6 flex-shrink-0">
        <div className="flex items-center gap-4">
          {/* Stop Button - Only show when activity is active */}
          {currentActivity && (
            <button
              onClick={handleCloseActivity}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
              Stop Activity
            </button>
          )}
          {!currentActivity && (
            <span className="text-sm text-neutral-500">Select an activity from the sidebar to start</span>
          )}
        </div>

        {/* Center - Activity Status */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {currentActivity && (
            <>
              <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-neutral-600 hidden sm:inline">Activity</span>
                <span className="text-neutral-600">Live</span>
              </div>
              <span className="text-neutral-300">|</span>
              <span className="text-xs sm:text-sm text-neutral-600">{activityResponses.length} <span className="hidden sm:inline">responses</span></span>
            </>
          )}
        </div>

        {/* Right - Session Info */}
        <div className="flex items-center gap-1.5 sm:gap-3 text-xs sm:text-sm text-neutral-500">
          <span>{participants}<span className="hidden sm:inline"> participants</span></span>
          <span className="text-neutral-300">|</span>
          <span>{elapsedTime.startsWith('Starts') ? elapsedTime : `${elapsedTime}`}<span className="hidden sm:inline"> elapsed</span></span>
        </div>
      </div>

      {/* Add Activity Modal - Slido Style with Two-Step Form */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setSelectedActivityType(null);
          setActivityFormData({
            question: '',
            options: ['', ''],
            correctAnswer: 0,
            timeLimit: 300,
          });
        }}
        title={selectedActivityType ? `Create ${activityTypes.find(t => t.type === selectedActivityType)?.name || 'Activity'}` : "Add new interaction"}
        size="lg"
      >
        {!selectedActivityType ? (
          // Step 1: Activity Type Selection
          <>
            <div className="grid grid-cols-2 gap-5">
              {activityTypes.map((actType) => (
                <button
                  key={actType.type}
                  onClick={() => {
                    setSelectedActivityType(actType.type);
                    // Set default options for poll/quiz
                    if (actType.type === 'poll' || actType.type === 'quiz') {
                      setActivityFormData({
                        question: '',
                        options: ['', '', '', ''],
                        correctAnswer: 0,
                        timeLimit: 300,
                      });
                    } else {
                      setActivityFormData({
                        question: '',
                        options: [],
                        correctAnswer: 0,
                        timeLimit: 300,
                      });
                    }
                  }}
                  className="group relative bg-white border-2 border-neutral-200 rounded-xl p-6 hover:border-primary-600 hover:shadow-lg transition-all text-left"
                >
                  {/* Activity Icon/Illustration */}
                  <div className="mb-5 w-full h-28 bg-white border-2 border-neutral-200 rounded-xl flex items-center justify-center group-hover:border-primary-600 transition-colors">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${getActivityTypeColor(actType.type)}`}>
                      {getActivityIcon(actType.type, 'lg')}
                    </div>
                  </div>

                  {/* Activity Info */}
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityTypeColor(actType.type)}`}>
                      {getActivityIcon(actType.type, 'sm')}
                    </div>
                    <h3 className="font-semibold text-base text-secondary-900">{actType.name}</h3>
                  </div>
                  <p className="text-sm text-neutral-600 leading-relaxed">{actType.description}</p>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 border-2 border-primary-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                </button>
              ))}
            </div>

            {/* Existing Activities Section */}
            {currentSubSession?.activities && currentSubSession.activities.length > 0 && (
              <div className="mt-10 pt-8 border-t border-neutral-200">
                <h3 className="text-sm font-semibold text-neutral-700 mb-4">Or launch an existing activity</h3>
                <div className="space-y-3 max-h-52 overflow-y-auto">
                  {currentSubSession.activities.map((activity) => {
                    const actType = activityTypes.find(t => t.type === activity.type);
                    return (
                      <button
                        key={activity.id}
                        onClick={() => {
                          handleLaunchActivity(activity);
                          setShowActivityModal(false);
                        }}
                        className="w-full p-4 rounded-xl border-2 border-neutral-200 hover:border-primary-600 hover:shadow-sm text-left transition-all flex items-center gap-4"
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityTypeColor(activity.type)}`}>
                          {getActivityIcon(activity.type, 'sm')}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-secondary-900 mb-0.5">{activity.title}</p>
                          <p className="text-xs text-neutral-500 capitalize">{activity.type.replace('_', ' ')}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          // Step 2: Activity Form
          <div className="space-y-6">
            {/* Back Button */}
            <button
              onClick={() => setSelectedActivityType(null)}
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to activity types
            </button>

            {/* Activity Type Header */}
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl">
              {(() => {
                const actType = activityTypes.find(t => t.type === selectedActivityType);
                return (
                  <>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActivityTypeColor(selectedActivityType)}`}>
                      {getActivityIcon(selectedActivityType, 'md')}
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900">{actType?.name}</h3>
                      <p className="text-sm text-neutral-500">{actType?.description}</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Question Input */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {selectedActivityType === 'wordcloud' ? 'Prompt' :
                 selectedActivityType === 'qa' ? 'Q&A Topic (optional)' :
                 selectedActivityType === 'challenges' ? 'What challenges would you like to collect?' :
                 selectedActivityType === 'actionplans' ? 'What action plans would you like to collect?' :
                 selectedActivityType === 'feedback' ? 'Feedback question' :
                 'Question'}
              </label>
              <textarea
                value={activityFormData.question}
                onChange={(e) => setActivityFormData({ ...activityFormData, question: e.target.value })}
                placeholder={
                  selectedActivityType === 'poll' ? 'e.g., How would you rate this session?' :
                  selectedActivityType === 'quiz' ? 'e.g., What is the capital of France?' :
                  selectedActivityType === 'wordcloud' ? 'e.g., Describe your experience in one word' :
                  selectedActivityType === 'openquestion' ? 'e.g., What did you learn today?' :
                  selectedActivityType === 'qa' ? 'e.g., Questions about today\'s topic' :
                  selectedActivityType === 'challenges' ? 'e.g., What challenges are you facing?' :
                  selectedActivityType === 'actionplans' ? 'e.g., What steps will you take after this session?' :
                  selectedActivityType === 'feedback' ? 'e.g., How was your overall experience?' :
                  'Enter your question...'
                }
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                rows={3}
              />
            </div>

            {/* Timer Input */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Time Limit
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={activityFormData.timeLimit}
                  onChange={(e) => setActivityFormData({ ...activityFormData, timeLimit: parseInt(e.target.value) })}
                  className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={60}>1 minute</option>
                  <option value={120}>2 minutes</option>
                  <option value={180}>3 minutes</option>
                  <option value={300}>5 minutes</option>
                  <option value={600}>10 minutes</option>
                  <option value={900}>15 minutes</option>
                  <option value={1800}>30 minutes</option>
                </select>
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{Math.floor(activityFormData.timeLimit / 60)} min</span>
                </div>
              </div>
            </div>

            {/* Allow Multiple Responses for Word Cloud */}
            {selectedActivityType === 'wordcloud' && (
              <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activityFormData.allowMultipleResponses}
                    onChange={(e) => setActivityFormData({ ...activityFormData, allowMultipleResponses: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
                <div>
                  <p className="text-sm font-medium text-neutral-700">Allow Multiple Responses</p>
                  <p className="text-xs text-neutral-500">Participants can submit words multiple times</p>
                </div>
              </div>
            )}

            {/* Options Input for Poll/Quiz */}
            {(selectedActivityType === 'poll' || selectedActivityType === 'quiz') && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Answer Options
                </label>
                <div className="space-y-3">
                  {activityFormData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {selectedActivityType === 'quiz' && (
                        <button
                          type="button"
                          onClick={() => setActivityFormData({ ...activityFormData, correctAnswer: index })}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            activityFormData.correctAnswer === index
                              ? 'border-green-500 bg-green-500 text-white'
                              : 'border-neutral-300 hover:border-green-400'
                          }`}
                          title={activityFormData.correctAnswer === index ? 'Correct answer' : 'Mark as correct'}
                        >
                          {activityFormData.correctAnswer === index && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...activityFormData.options];
                          newOptions[index] = e.target.value;
                          setActivityFormData({ ...activityFormData, options: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {activityFormData.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newOptions = activityFormData.options.filter((_, i) => i !== index);
                            const newCorrectAnswer = activityFormData.correctAnswer >= newOptions.length
                              ? newOptions.length - 1
                              : activityFormData.correctAnswer > index
                                ? activityFormData.correctAnswer - 1
                                : activityFormData.correctAnswer;
                            setActivityFormData({
                              ...activityFormData,
                              options: newOptions,
                              correctAnswer: newCorrectAnswer,
                            });
                          }}
                          className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {activityFormData.options.length < 6 && (
                  <button
                    type="button"
                    onClick={() => setActivityFormData({
                      ...activityFormData,
                      options: [...activityFormData.options, ''],
                    })}
                    className="mt-3 flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add option
                  </button>
                )}
                {selectedActivityType === 'quiz' && (
                  <p className="mt-3 text-xs text-neutral-500">
                    Click the circle to mark the correct answer
                  </p>
                )}
              </div>
            )}

            {/* Launch Button */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setSelectedActivityType(null);
                  setActivityFormData({
                    question: '',
                    options: ['', ''],
                    correctAnswer: 0,
                    timeLimit: 300,
                    allowMultipleResponses: false,
                  });
                }}
                className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate form
                  if (!activityFormData.question.trim()) {
                    alert('Please enter a question');
                    return;
                  }
                  if ((selectedActivityType === 'poll' || selectedActivityType === 'quiz') &&
                      activityFormData.options.filter(o => o.trim()).length < 2) {
                    alert('Please enter at least 2 options');
                    return;
                  }

                  // Get current subsession ID - need this to save the activity to the database
                  // If no subsession exists, auto-create a default one so trainers can
                  // launch activities directly from the live session without pre-setup
                  let currentSubId = currentSubSession?._id || currentSubSession?.id;
                  if (!currentSubId) {
                    try {
                      const subResponse = await trainerService.addSubSession(sessionId, {
                        name: 'Session',
                        duration: session?.duration || 120,
                        order: 1,
                      });
                      if (subResponse.success && subResponse.data) {
                        const newSub = subResponse.data;
                        currentSubId = newSub._id || newSub.id;
                        setCurrentSubSession(newSub);
                        setSession(prev => ({
                          ...prev,
                          subSessions: [...(prev?.subSessions || []), newSub],
                        }));
                      } else {
                        alert('Failed to create session section. Please try again.');
                        return;
                      }
                    } catch (err) {
                      console.error('[SessionControl] Error creating default subsession:', err);
                      alert('Failed to create session section. Please try again.');
                      return;
                    }
                  }

                  // Create the activity data
                  const actType = activityTypes.find(t => t.type === selectedActivityType);
                  const activityData = {
                    type: selectedActivityType,
                    title: activityFormData.question.substring(0, 50),
                    question: activityFormData.question,
                    timeLimit: activityFormData.timeLimit,
                    config: {
                      question: activityFormData.question,
                      options: activityFormData.options.filter(o => o.trim()),
                      correctAnswer: selectedActivityType === 'quiz' ? activityFormData.correctAnswer : undefined,
                      timeLimit: activityFormData.timeLimit,
                      allowMultipleResponses: selectedActivityType === 'wordcloud' ? activityFormData.allowMultipleResponses : undefined,
                    },
                  };

                  try {
                    // Save activity to database first to get MongoDB ObjectId
                    const response = await trainerService.addActivity(sessionId, currentSubId, activityData);
                    if (response.success && response.data) {
                      const savedActivity = response.data;

                      // Build full activity object with proper MongoDB _id
                      const newActivity = {
                        _id: savedActivity._id,
                        id: savedActivity._id,
                        type: selectedActivityType,
                        title: savedActivity.title || activityFormData.question.substring(0, 50),
                        name: `${actType?.name} Activity`,
                        question: activityFormData.question,
                        timeLimit: activityFormData.timeLimit,
                        duration: activityFormData.timeLimit,
                        options: activityFormData.options.filter(o => o.trim()).map((text, idx) => ({
                          text,
                          isCorrect: selectedActivityType === 'quiz' && idx === activityFormData.correctAnswer,
                        })),
                        config: savedActivity.config || activityData.config,
                      };

                      // Update local session state to include the new activity
                      setSession(prev => {
                        if (!prev?.subSessions) return prev;
                        const updatedSubSessions = prev.subSessions.map(sub => {
                          if ((sub._id || sub.id) === currentSubId) {
                            return {
                              ...sub,
                              activities: [...(sub.activities || []), newActivity],
                            };
                          }
                          return sub;
                        });
                        return { ...prev, subSessions: updatedSubSessions };
                      });

                      // Also update currentSubSession
                      setCurrentSubSession(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          activities: [...(prev.activities || []), newActivity],
                        };
                      });

                      // Now launch the activity with proper MongoDB ID
                      handleLaunchActivity(newActivity);
                    } else {
                      console.error('[SessionControl] Failed to save activity:', response);
                      alert('Failed to save activity. Please try again.');
                    }
                  } catch (error) {
                    console.error('[SessionControl] Error saving activity:', error);
                    alert('Error saving activity. Please try again.');
                  }

                  setShowActivityModal(false);
                  setSelectedActivityType(null);
                  setActivityFormData({
                    question: '',
                    options: ['', ''],
                    correctAnswer: 0,
                    timeLimit: 300,
                    allowMultipleResponses: false,
                  });
                }}
                className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                Launch Activity
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* QR Code Modal */}
      <Modal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        title="Session QR Code"
        size="lg"
      >
        <QRCodeGenerator
          sessionCode={session.code}
          sessionName={session.name}
          size={256}
          showDownload={true}
        />
      </Modal>

      {/* Participant List Panel */}
      <Modal
        isOpen={showParticipantPanel}
        onClose={() => setShowParticipantPanel(false)}
        title="Participants"
        size="lg"
      >
        <ParticipantListPanel
          participants={participantList}
          totalCount={participants}
          onSendMessage={handleSendMessage}
          onExportParticipants={handleExportParticipants}
        />
      </Modal>

      {/* Activity Settings Panel */}
      {selectedActivityForSettings && (
        <Modal
          isOpen={showActivitySettings}
          onClose={() => {
            setShowActivitySettings(false);
            setSelectedActivityForSettings(null);
          }}
          title={`Settings: ${selectedActivityForSettings.title}`}
          size="lg"
        >
          <ActivitySettingsPanel
            activity={selectedActivityForSettings}
            onUpdateSettings={handleUpdateActivitySettings}
            onClose={() => {
              setShowActivitySettings(false);
              setSelectedActivityForSettings(null);
            }}
          />
        </Modal>
      )}

      {/* End Session Success Modal */}
      <Modal
        isOpen={showEndSessionModal}
        onClose={() => {
          setShowEndSessionModal(false);
          navigate('/trainer/sessions');
        }}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Success Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-16 w-16 rounded-full border-2 border-success bg-white mb-4">
            <svg className="h-8 w-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-secondary-900 mb-3">
            Session Ended!
          </h3>

          {/* Message */}
          <p className="text-base text-neutral-600 mb-6">
            {endSessionMessage}
          </p>

          {/* OK Button */}
          <Button
            variant="primary"
            onClick={() => {
              setShowEndSessionModal(false);
              navigate('/trainer/sessions');
            }}
            fullWidth
            size="lg"
          >
            OK
          </Button>
        </div>
      </Modal>

      {/* End Subsession Confirmation Modal */}
      <Modal
        isOpen={showEndSubsessionModal}
        onClose={() => setShowEndSubsessionModal(false)}
        title="End Subsession"
        size="md"
      >
        <div className="py-4 px-2">
          {/* Warning Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-16 w-16 rounded-full border-2 border-warning bg-white mb-4">
            <svg className="h-8 w-8 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-secondary-900 mb-3 text-center">
            End "{currentSubSession?.name}"?
          </h3>

          {/* Message */}
          <p className="text-base text-neutral-600 mb-6 text-center">
            {currentActivity && currentActivity.status === 'active'
              ? <>You must end <strong>"{currentActivity.title || currentActivity.name || 'the current activity'}"</strong> before moving to the next subsession.</>
              : 'Are you sure you want to end this subsession?'}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowEndSubsessionModal(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEndSubsession}
              disabled={!!(currentActivity && currentActivity.status === 'active')}
              fullWidth
              className="bg-orange-600 hover:bg-orange-700"
            >
              End Subsession
            </Button>
          </div>
        </div>
      </Modal>

      {/* Break Time Modal - Trainer View */}
      <Modal
        isOpen={showBreakModal}
        onClose={() => {}} // Cannot close by clicking outside during break
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Break Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-5">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">
            Break Time
          </h3>

          {/* Section Name */}
          <p className="text-base text-neutral-600 mb-2">
            "{currentBreakInfo.name}" has ended
          </p>

          {/* Break Countdown */}
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-blue-600 font-medium mb-2">Time Remaining</p>
            <p className="text-5xl font-bold text-blue-700 font-mono">
              {Math.floor(breakTimeRemaining / 60).toString().padStart(2, '0')}:{(breakTimeRemaining % 60).toString().padStart(2, '0')}
            </p>
            <p className="text-sm text-blue-500 mt-2">
              {currentBreakInfo.breakTime} minute break
            </p>
          </div>

          {/* Message */}
          <p className="text-sm text-neutral-500 mb-6">
            Participants have been notified of the break. You cannot start the next subsession until the break is over.
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {breakTimeRemaining <= 0 ? (
              <Button
                variant="primary"
                onClick={handleBreakEnded}
                fullWidth
                size="lg"
              >
                Start Next Subsession
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={handleSkipBreak}
                  fullWidth
                  size="lg"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Skip Break & Continue
                </Button>
                <p className="text-xs text-neutral-400">
                  Skipping will notify participants that the break has ended early
                </p>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Overall Session Feedback Modal */}
      <Modal
        isOpen={showOverallFeedbackModal}
        onClose={() => overallFeedbackTrigger === 'manual' && setShowOverallFeedbackModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Feedback Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-5">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              {overallFeedbackTrigger === 'end' ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              )}
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">
            {overallFeedbackTrigger === 'end' ? 'All Subsessions Complete!' : 'Launch Overall Feedback'}
          </h3>

          {/* Message */}
          <p className="text-base text-neutral-600 mb-6">
            {overallFeedbackTrigger === 'end'
              ? 'Would you like to collect overall session feedback from participants before ending the session?'
              : 'Collect overall session feedback from all participants now?'}
          </p>

          {/* Info Box */}
          <div className="bg-green-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-green-800">
                Participants will be asked to rate the session and provide feedback. This will be shown in your session report.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={handleLaunchOverallFeedback}
              fullWidth
              size="lg"
            >
              Launch Overall Feedback
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkipOverallFeedback}
              fullWidth
              size="lg"
            >
              {overallFeedbackTrigger === 'end' ? 'Skip & End Session' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* End Session Warning Modal - Overall Feedback Not Launched */}
      <Modal
        isOpen={showEndSessionWarning}
        onClose={() => setShowEndSessionWarning(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Warning Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-amber-100 mb-5">
            <svg className="h-10 w-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">
            Overall Feedback Not Collected
          </h3>

          {/* Message */}
          <p className="text-base text-neutral-600 mb-6">
            You haven't collected overall session feedback from participants yet. Do you want to launch feedback before ending the session?
          </p>

          {/* Info Box */}
          <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-amber-800">
                Overall feedback helps you understand how participants felt about the entire session. This data will be included in your session report.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={handleLaunchFeedbackFromWarning}
              fullWidth
              size="lg"
            >
              Launch Overall Feedback
            </Button>
            <Button
              variant="secondary"
              onClick={handleEndSessionAnyway}
              fullWidth
              size="lg"
            >
              End Session Anyway
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowEndSessionWarning(false)}
              fullWidth
              size="lg"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Workbook Selection Modal */}
      <Modal
        isOpen={showWorkbookModal}
        onClose={() => setShowWorkbookModal(false)}
        title="Launch Workbook"
        size="lg"
      >
        <div className="py-3">
          <p className="text-sm text-neutral-600 mb-5 leading-relaxed">
            Select a workbook to share with participants. They will be able to view and download it during the session.
          </p>

          {session.workbooks && session.workbooks.length > 0 ? (
            <div className="space-y-4">
              {session.workbooks.map((workbook, index) => {
                const fileSizeMB = ((workbook.size || 0) / (1024 * 1024)).toFixed(2);
                const fileName = workbook.name || 'Untitled';
                const fileExtension = fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : 'FILE';
                const workbookId = workbook._id || workbook.id || `workbook-${index}`;

                // Icon color based on file type
                const iconColor = fileExtension === 'PDF' ? 'text-red-600 bg-red-50' :
                                 fileExtension === 'PPTX' || fileExtension === 'PPT' ? 'text-orange-600 bg-orange-50' :
                                 fileExtension === 'DOCX' || fileExtension === 'DOC' ? 'text-blue-600 bg-blue-50' :
                                 'text-neutral-600 bg-neutral-50';

                return (
                  <button
                    key={workbookId}
                    onClick={() => handleLaunchWorkbook(workbook)}
                    className="w-full p-5 bg-white border-2 border-neutral-200 rounded-xl hover:border-blue-400 hover:shadow-lg transition-all text-left group"
                  >
                    <div className="flex items-center gap-5">
                      {/* File Icon */}
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${iconColor}`}>
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>

                      {/* File Info */}
                      <div className="flex-1">
                        <h4 className="font-semibold text-base text-neutral-900 group-hover:text-blue-600 transition-colors mb-1.5">
                          {fileName}
                        </h4>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs font-semibold text-neutral-500 px-2.5 py-1 bg-neutral-100 rounded-md">
                            {fileExtension}
                          </span>
                          <span className="text-sm text-neutral-500">
                            {fileSizeMB} MB
                          </span>
                        </div>
                      </div>

                      {/* Launch Icon */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-neutral-100 rounded-xl mb-4">
                <svg className="w-10 h-10 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm text-neutral-600 leading-relaxed">No workbooks available for this session</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Relaunch Activity Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRelaunchConfirm}
        onClose={() => {
          setShowRelaunchConfirm(false);
          setPendingRelaunchActivity(null);
        }}
        onConfirm={handleConfirmRelaunch}
        title="Relaunch Activity?"
        message={`This activity "${pendingRelaunchActivity?.title || pendingRelaunchActivity?.name || 'Activity'}" has already received ${pendingRelaunchActivity?.responses?.length || pendingRelaunchActivity?.responseCount || 0} response(s). Relaunching will allow all participants (including those who already responded) to submit again. Do you want to continue?`}
        confirmText="Yes, Relaunch"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Delete Activity Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteActivityConfirm}
        onClose={() => {
          setShowDeleteActivityConfirm(false);
          setPendingDeleteActivity(null);
        }}
        onConfirm={confirmDeleteActivity}
        title="Delete Activity?"
        message={`Are you sure you want to delete "${pendingDeleteActivity?.title || pendingDeleteActivity?.name || pendingDeleteActivity?.question || 'this activity'}"? This action cannot be undone and all responses will be lost.`}
        confirmText={deletingActivity ? "Deleting..." : "Yes, Delete"}
        cancelText="Cancel"
        variant="danger"
      />

      {/* Active-Activity Alert — shown when trainer tries to launch another
          activity, or move to next subsession, while one is still live.
          Single OK button: pure acknowledgement, no destructive choice. */}
      <ConfirmDialog
        isOpen={!!activityActiveAlert}
        onClose={() => setActivityActiveAlert(null)}
        onConfirm={() => setActivityActiveAlert(null)}
        title={activityActiveAlert?.title || 'Stop the launched activity first'}
        message={activityActiveAlert?.message || ''}
        confirmText="Got it"
        cancelText="Close"
        variant="warning"
      />
    </div>
  );
};

export default SessionControlPage;
